// SPDX-License-Identifier: GPL-3.0-or-later
// The effects layer. It listens to the feedback events and draws on top of the table scene:
// bursts at the contact, glow halos, the ball trail, the lamp show, the small burst of a big
// moment, the reaction of a character and the boss of a stage (boss/index.ts). Play is calm: the
// layer makes no screen shake, no full-screen flash and no camera move. The backdrop is always
// still (backdrop.ts).
//
// The layer only reads. It never changes the physics or the rules, and no reader can change a
// game outcome. It runs in the render loop: call `update` once per frame BEFORE `renderer.render`.
//
// Rules (THEME.md section 11.3):
// - The level comes from the settings: "full", "reduced" or "off" (policy.ts). Reduced motion
//   lowers "full" to "reduced". Reduced and off have no lamp wash.
// - A lamp wash draws only when the flash limiter allows it (3 per second at most).
// - A hit glow, a halo and a character reaction each repeat at most 3 times per second.
// - Each effect has a text cue elsewhere (the live region).
// - The particle count has a hard cap. `update` makes no new objects.

import { Group, type PerspectiveCamera } from "three";
import type { RenderFrame } from "../../shared/contracts.ts";
import type {
  FeedbackBus,
  FeedbackEvent,
  FeedbackKind,
  FlashLimiter,
  SettingsStore,
} from "../../shared/feedback/index.ts";
import { type BossLayer, createBossLayer } from "../boss/index.ts";
import type { PinballRenderer } from "../renderer.ts";
import { createLampShow, type LampSlot } from "./lamps.ts";
import { characterHex, colorOf, hexOfKind, lampOfElement, modeHex, SHOW_HEX } from "./palette.ts";
import {
  type BurstShape,
  createParticlePool,
  createRandom,
  type ParticlePool,
} from "./particles.ts";
import { FULL_POLICY, policyFor, type VfxPolicy } from "./policy.ts";
import { createReactions } from "./reactions.ts";
import {
  BIG_HALO_SHAPE,
  CONTACT_SHAPES,
  contactCount,
  HALO_SHAPE,
  SHOW_COUNT,
  SHOW_SHAPE,
  tierOf,
} from "./shapes.ts";
import { createTrails } from "./trail.ts";

export type { LampPhase } from "./lamps.ts";
export { PALETTE_HEXES, TOKEN_HEX } from "./palette.ts";
export type { VfxPolicy } from "./policy.ts";
export { policyFor } from "./policy.ts";

/** The hard cap of the burst particles. */
export const BURST_CAP = 480;
/** The hard cap of the halos. */
export const HALO_CAP = 24;
/** A halo of one element repeats after this time at the earliest (3 times in 1 s at most). */
export const HALO_GAP_MS = 340;
/** Height of an effect above the playfield, in table units (the ball radius is 25). */
export const EFFECT_LIFT = 30;
/** Height of the centre of the table for an event that has no place. */
const CENTRE_HEIGHT = 60;
/** Pixel height that the scale uses when the renderer has no size yet. */
const DEFAULT_PIXEL_HEIGHT = 720;
/** Ball radius when the caller gives none. */
const DEFAULT_BALL_RADIUS = 25;

/** What `createVfx` needs. */
export interface VfxDeps {
  readonly renderer: Pick<
    PinballRenderer,
    "scene" | "camera" | "model" | "reducedMotion" | "pixelHeight"
  >;
  readonly bus: Pick<FeedbackBus, "subscribe">;
  readonly settings: Pick<SettingsStore, "get">;
  readonly limiter: Pick<FlashLimiter, "tryFlash">;
  /** A clock in ms for events that come before the first `update`. Default: 0. */
  readonly now?: () => number;
  /** A random source with the range [0, 1). Default: a seeded one. */
  readonly random?: () => number;
  /** The ball radius in table units. Default: 25. */
  readonly ballRadius?: number;
}

/** The effects layer. */
export interface Vfx {
  /** Move the effects on. Call it once per frame before `renderer.render`. */
  update(nowMs: number, dtMs: number, frame?: Pick<RenderFrame, "balls">): void;
  /** The reduced motion flag of the page. It adds to the flag of the renderer. */
  setReducedMotion(on: boolean): void;
  /** Stop to listen and remove every object. The camera goes back to rest. */
  dispose(): void;
  /** Live burst particles. */
  readonly liveParticles: number;
  /** Live halos. */
  readonly liveHalos: number;
  /** The policy now. */
  readonly policy: VfxPolicy;
}

/** The shared state of the effects. */
interface Fx {
  clock: number;
  policy: VfxPolicy;
  readonly pt: { x: number; y: number; z: number };
  readonly centre: { readonly x: number; readonly y: number; readonly z: number };
  readonly pool: ParticlePool;
  readonly halos: ParticlePool;
  readonly lamps: ReturnType<typeof createLampShow>;
  readonly reactions: ReturnType<typeof createReactions>;
  readonly limiter: Pick<FlashLimiter, "tryFlash">;
  readonly lastHalo: Map<string, number>;
}

const CONTACT_KINDS: ReadonlySet<FeedbackKind> = new Set<FeedbackKind>([
  "bumper",
  "sling",
  "target",
  "drop_bank_complete",
  "spinner",
  "ramp",
  "orbit",
  "lane",
  "kicker",
  "drain",
]);

const SHOW_KINDS: ReadonlySet<FeedbackKind> = new Set<FeedbackKind>([
  "jackpot",
  "multiball_start",
  "mode_start",
  "mode_complete",
  "skill_shot",
  "combo",
  "extra_ball",
  "high_score",
  "ball_lock",
  "ball_save",
  "tilt",
  "tilt_warning",
  "stage_start",
  "stage_clear",
  "perk_pick",
  "run_clear",
  "hurry_up_start",
  "hurry_up_collect",
  "frenzy_start",
  "combo_chain",
  "extra_ball_lit",
  "level_best",
]);

/** Moments that wash the lamps with a colour. */
const WASH_KINDS: ReadonlySet<FeedbackKind> = new Set<FeedbackKind>([
  "jackpot",
  "multiball_start",
  "mode_start",
  "mode_complete",
  "stage_clear",
  "run_clear",
  "hurry_up_collect",
  "frenzy_start",
]);

/** Moments that run the colour cycle of the light show. */
const CYCLE_KINDS: ReadonlySet<FeedbackKind> = new Set<FeedbackKind>([
  "jackpot",
  "multiball_start",
  "run_clear",
]);

/** Put the place of an event into `fx.pt`, in the scene frame. No place: the table centre. */
function locate(fx: Fx, ev: FeedbackEvent): void {
  const p = ev.pos;
  fx.pt.x = p ? p.x : fx.centre.x;
  fx.pt.y = p ? p.z + EFFECT_LIFT : fx.centre.y;
  fx.pt.z = p ? p.y : fx.centre.z;
}

function halo(fx: Fx, key: string, hex: number, shape: BurstShape): void {
  if (!fx.policy.halos) return;
  const last = fx.lastHalo.get(key);
  if (last !== undefined && fx.clock - last < HALO_GAP_MS) return;
  fx.lastHalo.set(key, fx.clock);
  fx.halos.burst(fx.pt.x, fx.pt.y, fx.pt.z, colorOf(hex), 1, shape);
}

function characterFx(fx: Fx, ev: FeedbackEvent): void {
  if (!fx.policy.reactions) return;
  const mount = fx.reactions.react(ev.characterId, fx.clock);
  if (!mount) return;
  fx.halos.burst(
    mount.position.x,
    mount.position.y + 60,
    mount.position.z,
    colorOf(characterHex(ev.characterId)),
    1,
    HALO_SHAPE,
  );
}

function contactFx(fx: Fx, ev: FeedbackEvent): void {
  locate(fx, ev);
  const hex = hexOfKind(ev.kind);
  const strength = ev.strength ?? 0.5;
  const count = contactCount(strength, fx.policy.bursts);
  fx.pool.burst(fx.pt.x, fx.pt.y, fx.pt.z, colorOf(hex), count, CONTACT_SHAPES[tierOf(strength)]);
  halo(fx, ev.element ?? ev.kind, hex, HALO_SHAPE);
  if (fx.policy.lamps) fx.lamps.hit(lampOfElement(ev.element), fx.clock);
  characterFx(fx, ev);
}

/** The colours of a table moment. A mode moment has the colour of its mode. */
function showHexes(ev: FeedbackEvent): readonly number[] {
  if (CYCLE_KINDS.has(ev.kind)) return SHOW_HEX;
  if (ev.kind === "mode_start" || ev.kind === "mode_complete") return [modeHex(ev.modeId)];
  return [hexOfKind(ev.kind)];
}

function showFx(fx: Fx, ev: FeedbackEvent): void {
  locate(fx, ev);
  const hexes = showHexes(ev);
  const first = hexes[0] ?? 0xffffff;
  const count = Math.round(SHOW_COUNT * fx.policy.bursts);
  for (const hex of hexes) {
    fx.pool.burst(fx.pt.x, fx.pt.y, fx.pt.z, colorOf(hex), count, SHOW_SHAPE);
  }
  halo(fx, ev.element ?? ev.kind, first, BIG_HALO_SHAPE);
  wash(fx, ev, colorOf(first));
  characterFx(fx, ev);
}

/** The lamp wash of a moment. The wash asks the flash limiter. A refused wash is skipped. */
function wash(fx: Fx, ev: FeedbackEvent, colour: ReturnType<typeof colorOf>): void {
  if (!fx.policy.wash || !WASH_KINDS.has(ev.kind)) return;
  if (fx.limiter.tryFlash()) fx.lamps.wash(colour, fx.clock);
}

/** The lamps of a scene with their places. */
function lampSlots(model: Pick<PinballRenderer["model"], "inserts" | "items">): LampSlot[] {
  const slots: LampSlot[] = [];
  for (const [name, insert] of model.inserts) {
    const object = model.items.get(name)?.object;
    slots.push({ name, insert, x: object?.position.x ?? 0, z: object?.position.z ?? 0 });
  }
  return slots;
}

function pixelsPerUnit(pixelHeight: number, camera: PerspectiveCamera): number {
  const height = pixelHeight > 0 ? pixelHeight : DEFAULT_PIXEL_HEIGHT;
  return height / (2 * Math.tan((camera.fov * Math.PI) / 360));
}

/** The DENY targets stand in a row. The boss stands at the middle of the row. */
const BOSS_TARGETS: readonly string[] = ["DenyD", "DenyE", "DenyN", "DenyY"];
/** Middle of the DENY row if the model has no such targets (table units). */
const BOSS_ANCHOR_FALLBACK = { x: 782.5, z: 920 };

/** The middle of the DENY targets in the scene frame. */
function bossAnchor(model: Pick<PinballRenderer["model"], "items">): { x: number; z: number } {
  let x = 0;
  let z = 0;
  let n = 0;
  for (const name of BOSS_TARGETS) {
    const object = model.items.get(name)?.object;
    if (object === undefined) continue;
    x += object.position.x;
    z += object.position.z;
    n++;
  }
  return n === 0 ? { ...BOSS_ANCHOR_FALLBACK } : { x: x / n, z: z / n };
}

/** What the effects layer keeps between calls. */
interface VfxCtx {
  readonly deps: VfxDeps;
  readonly fx: Fx;
  readonly boss: BossLayer;
  readonly trails: ReturnType<typeof createTrails>;
  readonly ballRadius: number;
  readonly group: Group;
  forcedReduced: boolean;
  disposed: boolean;
}

/** Read the settings and move the effects to the matching policy. */
function vfxSync(ctx: VfxCtx): VfxPolicy {
  const { deps, fx, trails } = ctx;
  const current = deps.settings.get();
  const next = policyFor(
    current.vfxLevel,
    ctx.forcedReduced || deps.renderer.reducedMotion,
    current.reduceFlashing,
  );
  if (next === fx.policy) return next;
  fx.policy = next;
  if (!next.trail) trails.clear();
  trails.cloud.points.visible = next.trail;
  if (next.bursts === 0) {
    fx.pool.clear();
    fx.halos.clear();
  }
  return next;
}

/** React to one feedback event. */
function vfxOnEvent(ctx: VfxCtx, ev: FeedbackEvent): void {
  if (ctx.disposed) return;
  vfxSync(ctx);
  const { fx } = ctx;
  ctx.boss.onEvent(ev, fx.policy);
  if (ev.kind === "ball_start") fx.lamps.setPhase("play");
  else if (ev.kind === "game_over") fx.lamps.setPhase("attract");
  else if (CONTACT_KINDS.has(ev.kind)) contactFx(fx, ev);
  else if (SHOW_KINDS.has(ev.kind)) showFx(fx, ev);
}

function vfxSetScale(ctx: VfxCtx): void {
  const { renderer } = ctx.deps;
  const scale = pixelsPerUnit(renderer.pixelHeight, renderer.camera);
  ctx.fx.pool.cloud.setScale(scale);
  ctx.fx.halos.cloud.setScale(scale);
  ctx.trails.cloud.setScale(scale);
}

function vfxUpdate(
  ctx: VfxCtx,
  nowMs: number,
  dtMs: number,
  frame?: Pick<RenderFrame, "balls">,
): void {
  if (ctx.disposed || !Number.isFinite(nowMs)) return;
  const { fx, trails } = ctx;
  fx.clock = nowMs;
  const policy = vfxSync(ctx);
  const dt = Math.min(Math.max(Number.isFinite(dtMs) ? dtMs : 0, 0), 100);
  vfxSetScale(ctx);
  fx.pool.update(dt);
  fx.halos.update(dt);
  if (policy.trail && frame) trails.update(frame.balls, dt, ctx.ballRadius * 1.6, 1);
  fx.lamps.update(nowMs, policy.lamps);
  fx.reactions.update(nowMs, policy.reactions);
  // The boss fades while it hides a ball. It needs the camera and the balls of the frame.
  const view = frame
    ? { camera: ctx.deps.renderer.camera, balls: frame.balls, ballRadius: ctx.ballRadius }
    : undefined;
  ctx.boss.update(dt, policy, view);
}

function vfxDispose(ctx: VfxCtx, unsubscribe: () => void): void {
  if (ctx.disposed) return;
  ctx.disposed = true;
  unsubscribe();
  const { fx } = ctx;
  const { renderer } = ctx.deps;
  fx.lamps.update(0, false);
  fx.reactions.reset();
  ctx.boss.dispose();
  renderer.scene.remove(ctx.group);
  fx.pool.cloud.dispose();
  fx.halos.cloud.dispose();
  ctx.trails.cloud.dispose();
}

/** Make the shared state of the effects from the dependencies. */
function vfxMakeFx(deps: VfxDeps): Fx {
  const { renderer } = deps;
  const random = deps.random ?? createRandom(0x5eed);
  const bounds = renderer.model.bounds;
  return {
    clock: deps.now?.() ?? 0,
    // The clouds start in the state of the full policy. `sync` changes them for another level.
    policy: FULL_POLICY,
    pt: { x: 0, y: 0, z: 0 },
    centre: {
      x: (bounds.left + bounds.right) / 2,
      y: CENTRE_HEIGHT,
      z: (bounds.top + bounds.bottom) / 2,
    },
    pool: createParticlePool(BURST_CAP, random),
    halos: createParticlePool(HALO_CAP, random),
    lamps: createLampShow(lampSlots(renderer.model)),
    reactions: createReactions(renderer.model.decorations),
    limiter: deps.limiter,
    lastHalo: new Map(),
  };
}

/** Make the effects layer. */
export function createVfx(deps: VfxDeps): Vfx {
  const fx = vfxMakeFx(deps);
  const trails = createTrails();
  const group = new Group();
  group.name = "vfx";
  group.add(fx.pool.cloud.points, fx.halos.cloud.points, trails.cloud.points);
  deps.renderer.scene.add(group);
  const boss = createBossLayer({
    parent: group,
    pool: fx.pool,
    anchor: bossAnchor(deps.renderer.model),
  });
  const ctx: VfxCtx = {
    deps,
    fx,
    boss,
    trails,
    ballRadius: deps.ballRadius ?? DEFAULT_BALL_RADIUS,
    group,
    forcedReduced: false,
    disposed: false,
  };
  vfxSync(ctx);
  const unsubscribe = deps.bus.subscribe((ev) => vfxOnEvent(ctx, ev));

  return {
    update(nowMs, dtMs, frame) {
      vfxUpdate(ctx, nowMs, dtMs, frame);
    },
    setReducedMotion(on) {
      ctx.forcedReduced = on;
      vfxSync(ctx);
    },
    dispose() {
      vfxDispose(ctx, unsubscribe);
    },
    get liveParticles() {
      return fx.pool.live;
    },
    get liveHalos() {
      return fx.halos.live;
    },
    get policy() {
      return fx.policy;
    },
  };
}
